import { isRunningInIframe } from './isRunningInIframe';
import { logger } from './logger';

/**
 * Altura que o app deve ocupar no lugar de `100vh`, em qualquer altura de tela cheia.
 *
 * Dentro de um iframe, `100vh` é a altura do *iframe*, não da área que o host de fato mostra.
 * Quando o parceiro dimensiona o iframe maior que o espaço visível — ou o recorta com
 * `overflow: hidden` —, tudo que é ancorado no fundo do viewport cai fora do recorte e fica
 * inalcançável. Esta var resolve para a faixa que o host mostra, e para `100vh` fora de iframe.
 *
 * Vale em qualquer ponto da árvore: dentro do `#root`, que é recortado e transladado, equivale
 * a `100%`; fora dele, à faixa visível.
 *
 * O deslocamento do topo (`--app-viewport-top`) não é exportado de propósito: dentro do `#root`
 * o `transform` aplicado por `index.css` já torna o elemento bloco de contenção dos
 * `position: fixed` internos, de modo que `top: 0` / `bottom: 0` já resolvem contra a faixa
 * visível. Somar o offset ali aplica a correção duas vezes e colapsa o elemento — um backdrop
 * com `top: X` e `bottom: 100vh − X − H` termina com altura zero. Só conteúdo em portal no
 * `body` precisa do offset, e para esse caso existe a classe `.app-viewport-layer`.
 */
// eslint-disable-next-line no-restricted-syntax -- única definição legítima do fallback `vh`.
export const APP_VIEWPORT_HEIGHT = 'var(--app-viewport-height, 100vh)';

/**
 * Fração da faixa visível, para usar no lugar de qualquer `Nvh`.
 *
 * `90vh` dentro de um iframe é 90% da altura do *iframe*; num host que só mostra uma faixa de
 * 400px de um iframe de 1000px, isso dá 900px — mais que o dobro do espaço real, e o elemento
 * vaza para fora da área visível.
 *
 * Note que o resultado é um comprimento absoluto, e não `%`: porcentagem depende de a cadeia
 * de pais ter altura definida, e só é equivalente para `position: fixed` dentro do `#root`.
 * Esta função vale em qualquer ponto da árvore, com ou sem `position: fixed`.
 *
 * Fora de iframe o `var()` cai no fallback e o valor volta a ser exatamente `Nvh`.
 *
 * @param fraction Fração da faixa visível, de 0 a 1. `1` devolve a faixa inteira.
 * @returns Comprimento CSS pronto para uso em qualquer propriedade de altura.
 *
 * @example
 * <Box height={appViewportHeight(0.9)} />
 * <Box maxH={`calc(${appViewportHeight(0.65)} - 270px)`} />
 */
export const appViewportHeight = (fraction = 1): string =>
  fraction === 1 ? APP_VIEWPORT_HEIGHT : `calc(${APP_VIEWPORT_HEIGHT} * ${fraction})`;

const HEIGHT_VAR = '--app-viewport-height';
const TOP_VAR = '--app-viewport-top';
const CLIPPED_FLAG = 'measured';
const PROBE_ATTR = 'data-app-viewport-probe';

/**
 * Piso absoluto da faixa considerada utilizável. Abaixo disso a medida quase sempre é
 * transitória (iframe entrando/saindo da tela) e encolher o app para nada só causa flicker.
 */
const MIN_USABLE_BAND = 240;

/**
 * O piso também é relativo: num iframe curto (300px) uma faixa de 150px é legítima, e um piso
 * fixo de 240px deixaria justamente esse parceiro sem correção. Vale o menor entre os dois.
 */
const MIN_USABLE_BAND_RATIO = 0.25;

/** Diferenças menores que isso são ruído de arredondamento e só causariam reflow à toa. */
const MIN_DELTA = 2;

/**
 * Folga para considerar o iframe "inteiro visível". Bordas do host e arredondamento de
 * subpixel tiram alguns pixels da interseção sem que exista recorte de verdade.
 */
const MIN_CLIP = 8;

/**
 * Granularidade do acompanhamento: o IntersectionObserver só dispara ao cruzar um threshold,
 * então a lista define de quantos em quantos por cento da altura do alvo a gente é avisado.
 *
 * A lista é densa de propósito — é o que faz o rodapé acompanhar a rolagem do host sem
 * degraus. O custo de disparar muito é neutralizado pelo `requestAnimationFrame`: por mais
 * callbacks que cheguem, no máximo uma escrita de CSS var por frame chega ao documento.
 */
const THRESHOLDS = Array.from({ length: 201 }, (_, index) => index / 200);

/**
 * Só uma medição ativa por documento. Protege contra StrictMode, hot reload e qualquer
 * chamada dupla acidental — duas sondas medindo e escrevendo as mesmas vars é receita para
 * oscilação.
 */
let activeTracking: (() => void) | null = null;

/**
 * Cria a sonda de medição.
 *
 * **Ela precisa ser um elemento próprio, e não o `#root`.** A medida publicada altera a
 * altura e a posição do `#root`; medir o `#root` faria a saída realimentar a entrada, e o
 * resultado é um sistema que só sabe encolher:
 *
 * - iframe de 1000px entrando na tela de um host de 600px: mede 400 → `#root` vira 400 →
 *   quando a faixa real cresce para 600 a interseção do `#root` continua 400 → o app fica
 *   preso em 400px para sempre;
 * - pior: se o `#root` for transladado para fora da faixa, a interseção vai a zero e nenhuma
 *   medida nova chega — tela em branco até o usuário rolar de volta.
 *
 * Um `position: fixed` com `inset: 0` no `body` cobre exatamente o viewport do iframe,
 * sempre, independente do que a gente publicou. `opacity: 0` (e não `visibility: hidden` nem
 * `display: none`) porque o elemento precisa continuar sendo renderizado para o
 * IntersectionObserver reportar um retângulo real.
 */
function createProbe(): HTMLElement {
  const probe = document.createElement('div');
  probe.setAttribute(PROBE_ATTR, '');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'width:100%',
    'height:100%',
    'margin:0',
    'padding:0',
    'border:0',
    'opacity:0',
    'pointer-events:none',
    'z-index:-1',
  ].join(';');
  return probe;
}

/**
 * A sonda só mede o viewport do iframe enquanto o `body` não for bloco de contenção de
 * `position: fixed`. Se alguém no futuro puser `transform`/`filter`/`perspective`/`contain`
 * no `body`, a medição passa a ser de outra coisa — em silêncio. Avisa em dev.
 */
function warnIfProbeIsContained(probe: HTMLElement): void {
  if (process.env.NODE_ENV === 'production') return;
  if (typeof getComputedStyle !== 'function') return;

  const parentStyle = getComputedStyle(probe.parentElement ?? document.body);
  const containing =
    parentStyle.transform !== 'none' ||
    parentStyle.filter !== 'none' ||
    parentStyle.perspective !== 'none' ||
    (parentStyle.willChange ?? '').includes('transform');

  if (containing) {
    logger.warn(
      'embeddedViewport: o container da sonda cria bloco de contenção para `position: fixed`. ' +
        'A faixa visível medida não corresponde mais ao viewport do iframe.',
    );
  }
}

/**
 * Descobre quanto do app está realmente visível quando ele roda embedado e publica isso em
 * CSS vars (`--app-viewport-height` / `--app-viewport-top`).
 *
 * O problema: dentro de um iframe, `100vh` é a altura do *iframe*, não da área que o host
 * mostra. Se o parceiro dimensiona o iframe maior que o espaço visível — ou recorta com
 * `overflow: hidden` —, tudo que a gente ancora na base do viewport (rodapé da tela, rodapé
 * dos drawers) cai fora do recorte e fica inalcançável.
 *
 * Como o host é cross-origin, não dá para ler o scroll nem o tamanho da janela dele. O
 * `IntersectionObserver` com root implícito é a única fonte: ele calcula a interseção com o
 * viewport do documento de topo, já recortada por todos os frames ancestrais, e entrega o
 * resultado em coordenadas do *nosso* viewport — que é exatamente o sistema de coordenadas
 * que o CSS do app precisa.
 *
 * Quem consome isso é o `#root` em index.css, que além da altura ganha um `transform` — é ele
 * que torna o #root bloco de contenção dos `position: fixed` internos (o rodapé da prescrição,
 * por exemplo), fazendo o "fundo do viewport" deles virar o fundo da faixa visível.
 *
 * Fora de iframe nada é escrito e os `var(..., 100vh)` caem no fallback, então o
 * comportamento de quem não embeda continua idêntico.
 *
 * @returns Função que encerra o acompanhamento (usada nos testes e no unmount).
 */
export function startEmbeddedViewportTracking(): () => void {
  const noop = () => undefined;

  if (!isRunningInIframe()) return noop;
  if (typeof IntersectionObserver === 'undefined') return noop;
  if (typeof document === 'undefined' || !document.body) return noop;

  // Uma medição por vez: a anterior é encerrada antes de a nova começar.
  activeTracking?.();

  const style = document.documentElement.style;
  const probe = createProbe();
  document.body.appendChild(probe);
  warnIfProbeIsContained(probe);

  let lastHeight = 0;
  let lastTop = 0;
  /**
   * `scheduled` é a fonte da verdade de "já existe um commit pendente"; `frameId` serve só
   * para cancelar. São duas variáveis de propósito: se o `requestAnimationFrame` executar o
   * callback de forma síncrona (fake timers, polyfill, jsdom mockado), o commit roda *antes*
   * de `frameId` receber o id, e usar o id como flag deixaria o agendador travado para sempre.
   */
  let scheduled = false;
  let frameId: number | null = null;
  let pending: { height: number; top: number; viewport: number } | null = null;
  let stopped = false;

  const clear = () => {
    lastHeight = 0;
    lastTop = 0;
    style.removeProperty(HEIGHT_VAR);
    style.removeProperty(TOP_VAR);
    delete document.documentElement.dataset.embeddedViewport;
  };

  const commit = () => {
    scheduled = false;
    frameId = null;
    const next = pending;
    pending = null;
    if (stopped || !next) return;

    /**
     * Iframe inteiro visível é o caso de quem embeda certo: aí a faixa é o próprio viewport e
     * não há nada a corrigir. Sair do caminho devolve o `100vh` original e mantém o
     * comportamento desses parceiros idêntico ao de antes — inclusive o bloco de contenção dos
     * `position: fixed`, que o `transform` do #root altera.
     */
    if (next.height >= next.viewport - MIN_CLIP && next.top <= MIN_CLIP) {
      if (lastHeight !== 0) clear();
      return;
    }

    if (Math.abs(next.height - lastHeight) < MIN_DELTA && Math.abs(next.top - lastTop) < MIN_DELTA) {
      return;
    }

    lastHeight = next.height;
    lastTop = next.top;
    style.setProperty(HEIGHT_VAR, `${next.height}px`);
    style.setProperty(TOP_VAR, `${next.top}px`);
    document.documentElement.dataset.embeddedViewport = CLIPPED_FLAG;
  };

  /**
   * Coalescência: durante uma rolagem do host chegam dezenas de callbacks por gesto, e cada
   * escrita nas vars invalida o layout da árvore inteira (o `#root` tem `height` + `transform`
   * dependentes delas). Guardar a última medida e aplicar uma vez por frame mantém a precisão
   * visual com uma fração do custo.
   */
  const schedule = (measurement: { height: number; top: number; viewport: number }) => {
    pending = measurement;
    if (scheduled) return;
    scheduled = true;
    if (typeof requestAnimationFrame !== 'function') {
      commit();
      return;
    }
    frameId = requestAnimationFrame(commit);
  };

  const measure = (entry: IntersectionObserverEntry) => {
    const band = entry.intersectionRect;
    // A sonda é `fixed; inset: 0`, então o retângulo dela *é* o viewport do iframe. Usamos ele
    // como referência (e não `window.innerHeight`) para que as duas medidas venham sempre da
    // mesma amostra — no mobile as duas divergem quando a barra do navegador recolhe.
    const viewport = Math.round(entry.boundingClientRect?.height || window.innerHeight);
    if (!band || !viewport) return;

    const height = Math.round(band.height);
    const top = Math.round(band.top);

    /**
     * Faixa degenerada (app fora da tela ou entrando nela): mantemos a última medida boa.
     * Isso é seguro justamente porque a sonda é independente do que publicamos — quando a
     * faixa voltar a ser utilizável chega uma medida nova e correta, inclusive maior.
     */
    if (height < Math.min(MIN_USABLE_BAND, viewport * MIN_USABLE_BAND_RATIO)) return;

    schedule({ height, top, viewport });
  };

  const observer = new IntersectionObserver(
    (entries) => measure(entries[entries.length - 1]),
    { threshold: THRESHOLDS },
  );
  observer.observe(probe);

  /**
   * O observer só reage a mudanças de interseção — e só é entregue enquanto a página está
   * sendo renderizada. Reobservar o alvo é o caminho garantido de forçar uma medida nova
   * quando o iframe muda de tamanho ou quando a aba volta a ficar visível (nesse intervalo o
   * host pode ter rolado ou redimensionado sem a gente ser avisado).
   */
  const remeasure = () => {
    if (stopped) return;
    // Defensivo: se alguma limpeza de DOM de terceiro tirou a sonda, recoloca antes de medir.
    if (!probe.isConnected) document.body.appendChild(probe);
    observer.unobserve(probe);
    observer.observe(probe);
  };
  window.addEventListener('resize', remeasure);
  window.addEventListener('orientationchange', remeasure);
  document.addEventListener('visibilitychange', remeasure);

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (frameId !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameId);
    scheduled = false;
    frameId = null;
    pending = null;
    observer.disconnect();
    window.removeEventListener('resize', remeasure);
    window.removeEventListener('orientationchange', remeasure);
    document.removeEventListener('visibilitychange', remeasure);
    probe.remove();
    clear();
    if (activeTracking === stop) activeTracking = null;
  };

  activeTracking = stop;
  return stop;
}
